// AdminUpiPayment.jsx
import React, { useState, useEffect, useContext, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Row, Col, Form, Button, Spinner, Badge, InputGroup } from "react-bootstrap";
import { CheckCircle, RotateCcw, XCircle } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import MyContext from "../../MyContext";
import Pagination from "../Whatsapp/Pagination";
import NoIndex from "../NoIndex";
import "./AdminDashboard.css";
import loader from "../../images/Anlook.svg";
import Swal from "sweetalert2";

const PAGE_SIZE = 200;

const AdminUpiPayment = ({ Admin }) => {
  /* ──────────────────────────── Context ─────────────────────────────── */
  const { baseUrl, config } = useContext(MyContext);

  /* ──────────────────────────── State ───────────────────────────────── */
  const [loading, setLoading] = useState(false);

  // Clients (kept if you later enable client filter)
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");

  // Date filters (default: yesterday ➜ today)
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(yesterday);
  const [endDate, setEndDate] = useState(today);

  // NEW: search & filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | success | failed | pending | etc.
  const [txnTypeFilter, setTxnTypeFilter] = useState("all"); // dynamic from data
  const [minAmt, setMinAmt] = useState("");
  const [maxAmt, setMaxAmt] = useState("");

  const [refundLoadingId, setRefundLoadingId] = useState(null);

  // UPI transactions
  const [upiTxns, setUpiTxns] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);

  /* ──────────────────────────── Fetch Clients ───────────────────────── */
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);

        // Get geolocation (optional)
        let latitude = null, longitude = null;
        if (navigator.geolocation) {
          await new Promise((resolve) =>
            navigator.geolocation.getCurrentPosition(
              ({ coords }) => {
                latitude = coords.latitude;
                longitude = coords.longitude;
                resolve();
              },
              () => resolve()
            )
          );
        }

        const payload = { userid: Admin.pkid, latitude, longitude };

        const { data } = await axios.post(
          `${baseUrl}/api/Admin/getAllClientList`,
          payload,
          config
        );

        if (data.responseCode === "00") {
          setClients(data.response);
        } else {
          toast.error(data.responseMessage);
        }
      } catch (err) {
        toast.error("Unable to load clients");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [Admin.pkid, baseUrl, config]);

  /* ──────────────────────────── Fetch UPI Txns ──────────────────────── */
  const fetchUpiTxns = async () => {
    try {
      setLoading(true);

      const body = {
        userid: selectedClientId, // can be empty if not filtering by client
        fromDate: startDate,
        toDate: endDate
      };

      const res = await fetch(
        `${baseUrl}/api/Transactions/v1.0/getUpiReportPaynnow`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...config.headers
          },
          body: JSON.stringify(body)
        }
      );

      const data = await res.json();
      if (data.responseCode === "00") {
        setUpiTxns(data.response || []);
        setPage(1); // reset pagination on new fetch
      } else {
        toast.error(data.responseMessage || "API error");
      }
    } catch (err) {
      toast.error("Failed to fetch transactions");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ──────────────────────────── Process Refund ──────────────────────── */
  const handleRefund = async (item) => {
    // choose a sensible amount: prefer transactionAmount, else creditAmount
    const amount = Math.trunc(Number(item?.creditAmount ?? item?.transactionAmount ?? 0)).toString();
    const transactionId = String(item?.refid ?? ""); // using refid to send

    const result = await Swal.fire({
      title: "Confirm Refund",
      html: `
        <div style="text-align:left">
          <div><b>Transaction ID:</b> ${transactionId}</div>
          <div><b>Amount:</b> ${amount}</div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, refund",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      allowOutsideClick: false,
    });

    if (!result.isConfirmed) return;

    try {
      setRefundLoadingId(transactionId);

      const payload = JSON.stringify({
        userid: "",                 // TODO: set if your API requires it
        amount: amount,
        transactionId: transactionId,
      });

      const res = await fetch(`${baseUrl}/api/Transactions/v1.0/refundProccess`, {
        method: "POST",
        headers: {
          ...config.headers,
        },
        body: payload,
        redirect: "follow",
      });

      const data = await res.json();

      if (data?.responseCode === "00") {
        await Swal.fire({
          title: "Refund Initiated",
          text: data?.responseMessage || "Refund processed successfully.",
          icon: "success",
        });
        // Optionally refresh
        // fetchUpiTxns();
      } else {
        await Swal.fire({
          title: "Refund Failed",
          text: data?.responseMessage || "Unable to process refund.",
          icon: "error",
        });
      }
    } catch (e) {
      await Swal.fire({
        title: "Error",
        text: "Something went wrong while processing the refund.",
        icon: "error",
      });
      console.error(e);
    } finally {
      setRefundLoadingId(null);
    }
  };

  /* ──────────────────────────── Derived Data ────────────────────────── */
  const sortedTxns = useMemo(
    () => [...upiTxns].sort((a, b) => new Date(b.indate) - new Date(a.indate)),
    [upiTxns]
  );

  // Build dynamic list of transaction types for filter dropdown
  const txnTypes = useMemo(() => {
    const set = new Set();
    sortedTxns.forEach((t) => {
      if (t?.transactiontype) set.add(String(t.transactiontype));
    });
    return ["all", ...Array.from(set)];
  }, [sortedTxns]);

  // Apply search & filters
  const filteredTxns = useMemo(() => {
    const q = search.trim().toLowerCase();
    const statusQ = statusFilter.toLowerCase();

    // Safe helpers
    const n = (v) => Number(v ?? 0);
    const s = (v) => (v ?? "").toString().toLowerCase();

    return sortedTxns.filter((t) => {
      // Search match against multiple fields
      const matchesSearch = !q
        ? true
        : [
            t.merchantTransactionid,
            t.refid,
            t.utnno,
            t.creditorAccount,
            t.ifsCcode,
            t.instructionIdentification,
          ]
            .map(s)
            .some((field) => field.includes(q));

      // Status filter
      const st = s(t.status);
      const matchesStatus =
        statusQ === "all" ? true : st === statusQ;

      // Txn type filter
      const matchesTxnType =
        txnTypeFilter === "all"
          ? true
          : s(t.transactiontype) === txnTypeFilter.toLowerCase();

      // Amount range filter (uses transactionAmount primarily)
      const amt = n(t.transactionAmount ?? t.creditAmount ?? 0);
      const minOk = minAmt === "" ? true : amt >= Number(minAmt);
      const maxOk = maxAmt === "" ? true : amt <= Number(maxAmt);

      return matchesSearch && matchesStatus && matchesTxnType && minOk && maxOk;
    });
  }, [sortedTxns, search, statusFilter, txnTypeFilter, minAmt, maxAmt]);

  const totalPages = Math.ceil(filteredTxns.length / PAGE_SIZE);
  const paginatedTxns = useMemo(
    () => filteredTxns.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredTxns, page]
  );

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTxnTypeFilter("all");
    setMinAmt("");
    setMaxAmt("");
    setPage(1);
  };

  /* ──────────────────────────── UI ──────────────────────────────────── */
  return (
    <div className="marginRight">
      <ToastContainer />
      <NoIndex />

      {loading ? (
        <div className="d-flex flex-column justify-content-center align-items-center vh-100">
          <Spinner animation="border" />
          <img src={loader} alt="loading" height={40} className="mt-3" />
        </div>
      ) : (
        <div
          className="p-4 bg-white"
          style={{ minHeight: "100vh", fontFamily: "Nunito,sans-serif" }}
        >
          {/* ── Top Cards ───────────────────────────────────────────── */}
          <div className="row admin-catalogue-wrapper">
            {/* Left Card */}
            <div className="col-md-4 p-0 m-0 admin-popup">
              <div className="admin-left-card text-white p-4 text-start rounded-2 h-100">
                <h5 className="fw-bold mb-2 admin-text-animate">
                  UPI Transactions
                </h5>
                <hr className="admin-divider" />
                <p className="mb-2 small admin-text-animate">
                  Monitor and manage UPI-based payments for each client.
                </p>
                <Badge bg="secondary">
                  Showing {filteredTxns.length} of {upiTxns.length}
                </Badge>
              </div>
            </div>

            {/* Right Card */}
            <div className="col-md-8 admin-popup">
              <div className="admin-right-card p-4 rounded-2 h-100">
                <h5 className="admin-restaurant-subtitle mb-3">Date Range</h5>
                <Row className="g-3 align-items-end">
                  {/* If you re-enable client filter later:
                  <Col md={4}>
                    <Form.Select
                      size="sm"
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                    >
                      <option value="">Select Client…</option>
                      {clients.map((c) => (
                        <option key={c.us_code} value={c.us_code}>
                          {`${c.us_code} : ${c.us_name}`}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  */}
                  <Col md={3}>
                    <Form.Label className="mb-1 small">Start</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      max={today}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="mb-1 small">End</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      max={today}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                  <Col md={2} className="d-grid">
                    <Button size="sm" onClick={fetchUpiTxns}>
                      Apply
                    </Button>
                  </Col>
                </Row>
              </div>
            </div>
          </div>

          {/* ── NEW: Search & Filters Row ──────────────────────────── */}
         <div className="mt-4 px-4">
  <Row className="g-3 align-items-end">
    <Col lg={3} md={4}>
      <Form.Label className="mb-1 small">Search</Form.Label>
      <InputGroup size="sm">
        <Form.Control
          placeholder="Txn ID / Ref ID / Account / IFSC / UTN / Identification"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        {search && (
          <Button
            variant="outline-secondary"
            onClick={() => setSearch("")}
          >
            <XCircle size={14} />
          </Button>
        )}
      </InputGroup>
    </Col>

    <Col lg={2} md={4} sm={6}>
      <Form.Label className="mb-1 small">Status</Form.Label>
      <Form.Select
        size="sm"
        value={statusFilter}
        onChange={(e) => {
          setStatusFilter(e.target.value);
          setPage(1);
        }}
      >
        <option value="all">All</option>
        <option value="success">Success</option>
        <option value="failed">Failed</option>
        <option value="pending">Pending</option>
      </Form.Select>
    </Col>

    <Col lg={2} md={4} sm={6}>
      <Form.Label className="mb-1 small">Txn Type</Form.Label>
      <Form.Select
        size="sm"
        value={txnTypeFilter}
        onChange={(e) => {
          setTxnTypeFilter(e.target.value);
          setPage(1);
        }}
      >
        {txnTypes.map((t) => (
          <option key={t} value={t}>
            {t === "all" ? "All" : t}
          </option>
        ))}
      </Form.Select>
    </Col>

    <Col lg={2} md={4} sm={6}>
      <Form.Label className="mb-1 small">Min Amount</Form.Label>
      <Form.Control
        size="sm"
        type="number"
        inputMode="decimal"
        placeholder="0"
        value={minAmt}
        onChange={(e) => {
          setMinAmt(e.target.value);
          setPage(1);
        }}
      />
    </Col>

    <Col lg={2} md={4} sm={6}>
      <Form.Label className="mb-1 small">Max Amount</Form.Label>
      <Form.Control
        size="sm"
        type="number"
        inputMode="decimal"
        placeholder="Any"
        value={maxAmt}
        onChange={(e) => {
          setMaxAmt(e.target.value);
          setPage(1);
        }}
      />
    </Col>

    {/* Clear button aligned at bottom */}
    <Col lg="auto" className="d-flex">
      <Button
        size="sm"
        variant="outline-secondary"
        onClick={clearFilters}
      >
        Clear
      </Button>
    </Col>
  </Row>
</div>


          {/* ── Table ───────────────────────────────────────────────── */}
          <div className="scrollable-table px-4 pt-4">
            <table className="table table-bordered table-hover">
              <thead className="text-center greenColour">
                <tr>
                  {[
                    "S.No.",
                    "Merchant Txn ID",
                    "Created On",
                    "Ref ID",
                    "UTN No",
                    "Credit Amt",
                    "Status",
                    "Txn Type",
                    "Creditor Acc",
                    "IFSC",
                    "Debit Amt",
                    "Balance",
                    "CGST",
                    "SGST",
                    "Charges",
                    "Txn Amt",
                  
                   
                  ].map((header) => (
                    <th key={header} className="p-2 th-style">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedTxns.map((item, idx) => (
                  <tr key={`${item.merchantTransactionid}-${idx}`}>
                    <td className="text-center">
                      {(page - 1) * PAGE_SIZE + idx + 1}
                    </td>

                    <td className="text-center">
                      <b>{item.merchantTransactionid}</b>
                    </td>
                    <td className="text-center">{item.createdOn}</td>
                    <td className="text-center">
                      <b>{item.refid}</b>
                    </td>

                    <td className="text-center">{item.utnno}</td>
                    <td className="text-center">{item.creditAmount}</td>

                    <td className="text-center">
                      {item.status?.toLowerCase() === "success" ? (
                        <Badge
                          bg="success"
                          className="d-inline-flex align-items-center gap-1"
                        >
                          <CheckCircle size={14} />
                          Success
                        </Badge>
                      ) : (
                        item.status
                      )}
                    </td>

                    <td className="text-center">{item.transactiontype}</td>
                    <td className="text-center">{item.creditorAccount}</td>
                    <td className="text-center">{item.ifsCcode?.toUpperCase()}</td>
                    <td className="text-center">{item.debitAmount}</td>
                    <td className="text-center">{item.balance}</td>
                    <td className="text-center">{item.cgstAmount || 0}</td>
                    <td className="text-center">{item.sgstAmount || 0}</td>
                    <td className="text-center">{item.chargesAmount || 0}</td>
                    <td className="text-center">{item.transactionAmount || 0}</td>
                   
                   
                  </tr>
                ))}

                {paginatedTxns.length === 0 && (
                  <tr>
                    <td className="text-center py-4" colSpan={18}>
                      No records found for current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ────────────────────────────────────────── */}
          {totalPages > 1 && (
            <div className="pb-4 px-4">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageClick={setPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminUpiPayment;
